import { SelectOption } from '~/shared/utils/types';
import {
  AUTOSCALING_ELIGIBILITY_PARAM_KEY,
  AUTOSCALING_STATE_PARAM_KEY,
  createAutoscalingStateFilters,
  createEligibilityFilters,
} from '~/shared/utils/autoscalingFilterUtils';

import { bytesInTib } from './constants';
import { hasCapacityFilter, type VolumeFilterPanelState } from './panelState';

export {
  AUTOSCALING_ELIGIBILITY_PARAM_KEY,
  AUTOSCALING_STATE_PARAM_KEY,
  createAutoscalingStateFilters,
  createEligibilityFilters,
};

export type FilterStoredInURL = Omit<SelectOption, 'group'> & {
  group: SelectOption['group'];
};

function normalizeAttachedToValue(value: string): string {
  return value === 'ec2' || value === 'instances' ? 'instance' : value;
}

const attachedToAllValues = new Set(['unattached', 'instance', 'cluster']);

export function createCollapsedAutoscalingStateFilters(stateIds: string[]): SelectOption[] {
  const mapped = createAutoscalingStateFilters(stateIds);

  if (mapped.length === 0) {
    return [];
  }

  const hasOn = mapped.some((m) => m.id === 'on');
  const hasOff = mapped.some((m) => m.id === 'off');

  // Collapse on+off into one synthetic filter so getApiFilters emits {field: 'autoscalingState', value: 'on,off', op: 'equals'}
  if (hasOn && hasOff) {
    return [{ id: 'on,off', name: 'on,off', group: 'autoscalingState' }];
  }

  return mapped;
}

/** Every group the panel owns, and therefore rebuilds from pending state on each apply. */
const panelOwnedGroups: ReadonlySet<string> = new Set<SelectOption['group']>([
  'volumeId',
  'instanceId',
  'clusterName',
  'accountId',
  'autoscalingState',
  'supported',
  'attachedTo',
  'region',
  'infrastructure',
  'nodeGroupName',
  'diskType',
  'diskSizeMin',
  'diskSizeMax',
]);

/**
 * Strips the groups the panel owns out of whatever the URL already held, so an apply replaces
 * them rather than appending a second copy. Anything else in the URL is passed through
 * untouched.
 */
export function filterNonSpecificGroups(filters: FilterStoredInURL[]): SelectOption[] {
  return filters.filter((f) => {
    const isTagGroup = typeof f.group === 'string' && f.group.startsWith('tag:');
    return !panelOwnedGroups.has(f.group) && !isTagGroup;
  });
}

/** TiB (what the slider works in) → bytes (what `Volume.diskSize` reports). */
function capacityOption(tib: number, group: 'diskSizeMin' | 'diskSizeMax'): SelectOption {
  const bytes = String(Math.round(tib * bytesInTib));
  return { id: bytes, name: bytes, group };
}

/**
 * The capacity range as API filters — a `gte`/`lte` pair, or nothing at all when both handles
 * still sit on their bounds.
 */
export function buildCapacityApiFilters(pending: VolumeFilterPanelState): SelectOption[] {
  if (!hasCapacityFilter(pending)) {
    return [];
  }

  return [
    capacityOption(pending.capacityMin, 'diskSizeMin'),
    capacityOption(pending.capacityMax, 'diskSizeMax'),
  ];
}

/**
 * Concatenates the panel's selections in a fixed order. That order is part of the URL's
 * contract and is pinned by the test suite — new dimensions are APPENDED rather than slotted
 * into panel order, so existing links keep serializing identically.
 *
 * Takes the panel state as one object rather than a dozen positional lists: most of them are
 * `SelectOption[]`, so a transposed pair would have type-checked and silently filtered by the
 * wrong dimension.
 */
export function combineFilters(
  baseApiFilters: SelectOption[],
  pending: VolumeFilterPanelState
): SelectOption[] {
  const autoscalingApiFilters = buildAutoscalingApiFilters(
    pending.autoscaleState,
    pending.eligibility
  );

  const normalizedAttachedToFilters = pending.infrastructureFilters.map((f) =>
    f.group === 'attachedTo'
      ? { ...f, id: normalizeAttachedToValue(f.id), name: normalizeAttachedToValue(f.name) }
      : {
        ...f,
        id: normalizeAttachedToValue(f.id),
        name: normalizeAttachedToValue(f.name),
        group: 'attachedTo' as const,
      }
  );

  // The three attachedTo buckets are mutually exclusive and exhaustive.
  // Selecting all of them is equivalent to no attachedTo filter.
  const shouldOmitAttachedTo =
    normalizedAttachedToFilters.length === attachedToAllValues.size &&
    normalizedAttachedToFilters.every((f) => attachedToAllValues.has(f.id));

  const effectiveAttachedToFilters = shouldOmitAttachedTo ? [] : normalizedAttachedToFilters;

  return [
    ...baseApiFilters,
    ...pending.clusterNameFilters,
    ...pending.volumeIdFilters,
    ...pending.volumeTagFilters,
    ...pending.accountFilters,
    ...pending.instanceFilters,
    ...autoscalingApiFilters.filter((f) => f.group === 'autoscalingState'),
    ...autoscalingApiFilters.filter((f) => f.group === 'supported'),
    ...pending.regionFilters,
    ...effectiveAttachedToFilters,
    ...pending.nodeGroupFilters,
    ...pending.diskTypeFilters,
    ...buildCapacityApiFilters(pending),
  ];
}

export function buildAutoscalingApiFilters(
  stateIds: string[],
  eligibilityIds: string[]
): SelectOption[] {
  return [
    ...createCollapsedAutoscalingStateFilters(stateIds),
    ...createEligibilityFilters(eligibilityIds),
  ];
}
